import { Rocket, Star, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { memo } from "react";
import { motion } from "framer-motion";
import Banner from "@/components/Banner";
import InstallPWA from "@/components/InstallPWA";

const Hero = memo(() => {
  return (
    <>
      <Banner />
      <div className="bg-cyan-200 py-4 md:py-10 relative overflow-hidden">
        {/* Cloud/bubble decorations */}
        <div className="absolute top-4 left-[10%] w-20 h-12 bg-white/20 rounded-full blur-sm" />
        <div className="absolute top-8 right-[15%] w-16 h-10 bg-white/15 rounded-full blur-sm" />
        <div className="absolute bottom-2 left-[30%] w-24 h-14 bg-white/10 rounded-full blur-sm" />

        <motion.div
          className="absolute top-3 left-8 text-white/30"
          animate={{ y: [0, -8, 0], rotate: [0, 15, 0] }}
          transition={{ duration: 3, repeat: Infinity }}
        >
          <Star size={28} fill="currentColor" />
        </motion.div>
        <motion.div
          className="absolute bottom-3 right-12 text-yellow-200/40"
          animate={{ y: [0, -6, 0], scale: [1, 1.2, 1] }}
          transition={{ duration: 2.5, repeat: Infinity }}
        >
          <Sparkles size={24} />
        </motion.div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-3 gap-y-1 overflow-visible text-center text-[26px] leading-[1.15] sm:text-3xl md:text-5xl lg:text-[3.35rem] xl:text-6xl font-fredoka font-bold text-slate-900 drop-shadow-[0_2px_0_rgba(255,255,255,0.95)]">
              <span className="inline-block whitespace-nowrap">
                Fun Learning
              </span>{" "}
              <span className="inline-block whitespace-nowrap text-orange-700 drop-shadow-[0_2px_0_rgba(255,255,255,0.9)]">for Kids</span>{" "}
              <span className="inline-block whitespace-nowrap text-purple-800 drop-shadow-[0_2px_0_rgba(255,255,255,0.9)]">
                Ages 3–8
              </span>{" "}
              <span className="inline-block" aria-hidden="true">🌟</span>
            </h1>

            <p className="mt-2 md:mt-3 text-sm md:text-lg text-cyan-950 max-w-2xl mx-auto font-bold drop-shadow-[0_1px_0_rgba(255,255,255,0.5)] px-2">
              Games, stories, worksheets & videos in one playful app.
            </p>

            <div className="mt-3 md:mt-4 flex flex-wrap justify-center gap-2.5">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/alphabet"
                  className="bg-orange-400 hover:bg-orange-500 text-white px-5 py-2.5 rounded-full font-bold text-sm shadow-lg flex items-center gap-1.5 transition-colors border-2 border-orange-300"
                >
                  Start Free <Rocket size={16} />
                </Link>
              </motion.div>
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <InstallPWA variant="compact" />
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Bottom cloud wave */}
        <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none">
          <svg viewBox="0 0 1440 60" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-8 md:h-12" preserveAspectRatio="none">
            <path d="M0 60L60 50C120 40 240 20 360 15C480 10 600 20 720 25C840 30 960 20 1080 15C1200 10 1320 15 1380 17L1440 20V60H0Z" fill="#ecfeff" />
          </svg>
        </div>
      </div>
    </>
  );
});

Hero.displayName = "Hero";

export default Hero;
